
import React from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Globe, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface LanguageSwitcherProps {
  className?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "custom-red" | null | undefined;
  'aria-label'?: string;
  isMobile?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  className, 
  variant = "ghost", 
  "aria-label": ariaLabel,
  isMobile = false 
}) => {
  const { t, i18n, changeLanguage } = useTranslationUtils();
  
  // Define languages with their native names
  const languages = [
    { code: 'fr', name: 'Français' },
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' }
  ];

  const handleLanguageChange = (value: string) => {
    changeLanguage(value);
    console.log(`Language changed to: ${value}`);
  };

  // Get current flag emoji based on language code
  const getCurrentFlagEmoji = () => {
    switch(i18n.language) {
      case 'fr': return '🇫🇷';
      case 'en': return '🇬🇧';
      case 'es': return '🇪🇸';
      default: return '🇫🇷';
    }
  };

  // Get current language name in its native form
  const getCurrentLanguageName = () => {
    const langCode = i18n.language;
    const language = languages.find(lang => lang.code === langCode);
    return language ? language.name : 'Français';
  };

  return (
    <div className={`flex items-center ${className || ''} ${isMobile ? 'mx-auto my-2' : ''}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant={variant}
            size="sm" 
            className="gap-2 font-medium"
            aria-label={ariaLabel || t('language.changeLanguage')}
            title={t('language.changeLanguage')}
          >
            <Globe className="h-4 w-4" />
            <span>{getCurrentFlagEmoji()} {getCurrentLanguageName()}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900 z-50">
          {languages.map((lang) => {
            const isActive = i18n.language === lang.code;
            const flagEmoji = lang.code === 'fr' ? '🇫🇷' : lang.code === 'en' ? '🇬🇧' : '🇪🇸';
            
            return (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`cursor-pointer font-medium flex items-center justify-between ${
                  isActive ? 'bg-slate-100 dark:bg-slate-800' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  {flagEmoji} {lang.name}
                </span>
                {isActive && <Check className="h-4 w-4 text-green-600" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default LanguageSwitcher;
